/**
 * TelehealthHubPage.js — Unified telehealth surface.
 *
 * Phase 1 of the Telehealth + Telemedicine consolidation. The previous
 * UI exposed two confusingly-named sidebar entries:
 *
 *   • "Telehealth"             → TelehealthPage  (WebRTC video calls via routes/voip.js)
 *   • "Telemedicine Scheduling" → TelemedicinePage (scheduling/admin via routes/telemedicine.js)
 *
 * They aren't duplicates — they're sequential phases of the same patient
 * encounter (schedule the visit, then run the live call). Splitting them
 * into two top-level sidebar items forced staff to context-switch between
 * "where do I look up the appointment?" and "where do I actually join the
 * call?" That UX confusion is what we're fixing here.
 *
 * Architecture: this page is a thin shell — each tab embeds the existing
 * page component (TelehealthPage = live call, TelemedicinePage = schedule).
 * Zero changes to either backend, both Socket.io and the telemed:* IPC
 * handlers continue to work exactly as before. Same playbook as the
 * Messages and Activity consolidations.
 */
import React, { useState } from 'react';
import { Calendar, Video, Lock } from 'lucide-react';
import TelehealthPage from './TelehealthPage';
import TelemedicinePage from './TelemedicinePage';

const TABS = [
  { id: 'schedule', label: 'Schedule & Sessions', icon: Calendar, perm: 'telemed:read' },
  { id: 'live',     label: 'Live Call',           icon: Video,    perm: 'telemed:read' },
];

export default function TelehealthHubPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  // A pending video-room hand-off from My Day → Calendar means we should drop
  // straight onto the Live Call tab so TelehealthPage can auto-join it.
  let hasPendingVideoRoom = false;
  try { hasPendingVideoRoom = !!sessionStorage.getItem('pendingVideoRoom'); } catch { hasPendingVideoRoom = false; }

  // Default to the schedule tab — most users come here to look at upcoming
  // visits, not to immediately drop into a video call. The 'live' redirect
  // path lets the old /telehealth bookmark land on the call surface.
  const defaultTab = (hasPendingVideoRoom && visibleTabs.find((t) => t.id === 'live')?.id)
    || visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'schedule';

  const [activeTab, setActiveTab] = useState(defaultTab);

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Telehealth</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Lock size={48} />
          <p>You don't have permission to view telehealth sessions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page telehealth-hub-page">
      <div className="telehealth-hub-header">
        <h1 className="page-title">Telehealth</h1>
      </div>

      <div className="telehealth-hub-tabs" role="tablist" aria-label="Telehealth sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`telehealth-hub-panel-${tab.id}`}
              className={`telehealth-hub-tab ${isActive ? 'telehealth-hub-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="telehealth-hub-panel"
        id={`telehealth-hub-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'schedule' && (
          <div className="telehealth-hub-embedded">
            <TelemedicinePage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'live' && (
          <div className="telehealth-hub-embedded">
            <TelehealthPage user={user} onNavigate={onNavigate} />
          </div>
        )}
      </div>

      <style jsx>{`
        .telehealth-hub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .telehealth-hub-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .telehealth-hub-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .telehealth-hub-tab:hover {
          color: #2E8B98;
        }
        .telehealth-hub-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        .telehealth-hub-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .telehealth-hub-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}
